import { useEffect, useMemo, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { RaidPracticeDeckPicker } from './RaidPracticeDeckPicker.jsx';

function emptyHit(bossNo) {
  return { bossNo: Number(bossNo || 1), damageEok: '', deck: Array(5).fill(''), title: '' };
}

function normalizedCodes(deck) {
  return (deck || []).map((code) => String(code || '').trim()).filter(Boolean);
}

export function RaidPracticeThreeHitEditor({
  bosses = [],
  initialBossNo = 1,
  seasonNo = '',
  nickname = '',
  mobile = false,
  working = '',
  onSave,
  onClose,
}) {
  const [hits, setHits] = useState(() => [emptyHit(initialBossNo)]);
  const [error, setError] = useState('');
  const saving = working === 'save:three-hit';

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeByEscape = (event) => { if (event.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', closeByEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeByEscape);
    };
  }, [onClose]);

  const usedAcrossHits = useMemo(() => hits.map((hit) => normalizedCodes(hit.deck)), [hits]);
  const updateHit = (index, patch) => setHits((current) => current.map((hit, hitIndex) => (hitIndex === index ? { ...hit, ...patch } : hit)));
  const removeHit = (index) => setHits((current) => current.filter((_, hitIndex) => hitIndex !== index));
  const addHit = () => {
    if (hits.length >= 3) return;
    setHits((current) => [...current, emptyHit(current.at(-1)?.bossNo || initialBossNo)]);
  };

  const submit = async () => {
    const allCodes = hits.flatMap((hit) => normalizedCodes(hit.deck).map((code) => code.toLocaleLowerCase('ko-KR')));
    const duplicate = allCodes.find((code, index) => allCodes.indexOf(code) !== index);
    if (duplicate) { setError('3타 전체에서 동일한 니케를 중복 사용할 수 없습니다.'); return; }
    for (let index = 0; index < hits.length; index += 1) {
      const hit = hits[index];
      const damage = Number(hit.damageEok);
      if (!Number.isInteger(Number(hit.bossNo))) { setError(`${index + 1}타의 보스를 선택해주세요.`); return; }
      if (!Number.isFinite(damage) || damage <= 0) { setError(`${index + 1}타의 딜량을 0보다 크게 입력해주세요.`); return; }
      if (normalizedCodes(hit.deck).length !== 5) { setError(`${index + 1}타의 니케 5명을 모두 선택해주세요.`); return; }
    }
    setError('');
    const ok = await onSave?.(hits.map((hit) => ({
      ...hit,
      title: String(hit.title || '').trim(),
      damageEok: Number(hit.damageEok),
      deck: normalizedCodes(hit.deck),
    })));
    if (ok) onClose?.();
  };

  return <div className={`raid-practice-three-hit-overlay ${mobile ? 'mobile' : 'desktop'}`} role="presentation">
    <section className="raid-practice-three-hit-dialog" role="dialog" aria-modal="true" aria-label="3타 모의전 작성">
      <header className="raid-practice-three-hit-head">
        <div>
          <h2>{nickname || '내'} · 3타 모의전 입력</h2>
        </div>
        <div className="raid-practice-three-hit-head-actions">
          <button
            type="button"
            className="ui-icon-action save"
            disabled={Boolean(working)}
            onClick={submit}
            aria-label={saving ? '저장 중' : '3타 저장'}
            title={saving ? '저장 중…' : '3타 저장'}
          >
            <DororaidIcon name="save" size={20} />
          </button>
          <button type="button" className="ui-icon-action close" onClick={onClose} aria-label="닫기" title="닫기"><DororaidIcon name="close" size={20} /></button>
        </div>
      </header>

      <div className="raid-practice-three-hit-scroll">
        <p className="raid-practice-three-hit-guide">각 타수마다 제목·보스·딜량·니케 5명을 입력합니다. 세 덱 전체에서는 동일한 니케를 사용할 수 없습니다.</p>

        {hits.map((hit, index) => {
          const excludedCodes = usedAcrossHits.flatMap((codes, hitIndex) => (hitIndex === index ? [] : codes));
          const boss = bosses.find((item) => Number(item.bossNo) === Number(hit.bossNo));
          const selectedCount = normalizedCodes(hit.deck).length;
          return <section className="raid-practice-three-hit-card" key={index}>
            <div className="raid-practice-three-hit-card-head">
              <div><span>ATTACK {String(index + 1).padStart(2, '0')}</span><strong>{index + 1}타</strong></div>
              {hits.length > 1 ? <button type="button" className="danger" onClick={() => removeHit(index)}>삭제</button> : null}
            </div>

            <div className="raid-practice-three-hit-form-card">
              <label style={{ gridColumn: '1 / -1' }}><span>제목</span><input type="text" maxLength={100} value={hit.title} onChange={(event) => updateHit(index, { title: event.target.value })} placeholder="고점덱 / 막타덱" /></label>
              <label><span>보스 선택</span><select value={hit.bossNo} onChange={(event) => updateHit(index, { bossNo: Number(event.target.value) })}>{bosses.map((item) => <option value={item.bossNo} key={item.bossNo}>{item.bossNo}보스 · {bossDisplayName(item)}</option>)}</select></label>
              <label><span>딜량(억)</span><input type="number" min="0.001" step="0.001" inputMode="decimal" value={hit.damageEok} onChange={(event) => updateHit(index, { damageEok: event.target.value })} placeholder="0" /></label>
            </div>

            <section className="raid-practice-three-hit-squad">
              <div className="raid-practice-three-hit-squad-head"><div><h3>니케 선택</h3><small>다른 타수에서 선택한 니케는 선택 목록에서 제외됩니다.</small></div><b>{selectedCount}/5</b></div>
              <RaidPracticeDeckPicker value={hit.deck} onChange={(deck) => updateHit(index, { deck })} excludedCodes={excludedCodes} mobile={mobile} />
            </section>

            <div className="raid-practice-three-hit-context">
              {seasonNo ? <span>{seasonNo}차</span> : null}
              <span>{boss ? bossDisplayName(boss) : '보스 선택'}</span>
              <span>{boss?.element || '-'}</span>
            </div>
          </section>;
        })}

        {hits.length < 3 ? <button type="button" className="raid-practice-three-hit-add" onClick={addHit}><span>+</span><strong>슬롯 추가</strong></button> : null}
        {error ? <p className="raid-practice-three-hit-error" role="alert">{error}</p> : null}
      </div>
    </section>
  </div>;
}
